import { FC, useCallback, useEffect, useMemo, useState } from "react"

import { SitesSummaryTable } from "./SitesSummaryTable"
import { SiteInitForm } from "./SiteInitForm"
import { FloatButton, Modal, Radio, Tabs, Tooltip, message } from "antd"
import { Translated } from "../../utils/translated"
import { FlexCol, FlexRow } from "../../components/commons-ts/common"
import { NormType, Site } from "../../generated/proto-ts/main"
import { useUsercommContextBLE } from "../../usercomm/local/ble/usercommProviderBLE"
import { CENTRAL_CONTAINER_GAP, pbUUIDToUuid } from "../../utils/utils"
import { useLocation } from "react-router-dom"
import { PlusOutlined } from "@ant-design/icons"
import { ImpactsSummaryTable } from "./ImpactsSummaryTable"
import { DataCloudSyncWidget } from "../../components/commons-ts/dataCloudSyncWidget"
import {
  useUsercommDeleteSiteBimodal,
  useUsercommSitesBimodal,
  useUsercommSyntheticImpactWithEmbeddedReferencesBimodal,
} from "../../usercomm/common/usercommAsyncRequestBimodal"
import { useSyncronizationContext } from "../../providers/syncronizationProvider"
import { SyntheticImpactWithEmbeddedReferences } from "../../usercomm/common/usercommAsyncRequestGeneric"
import { useUsercommGetStationConfigBLE } from "../../usercomm/local/ble/usercommAsyncRequestBLE"
import { ESummaryTableFilterType, ESummaryTableType } from "../../types"
import { SitesClusterMap } from "../../components/commons-ts/googleMaps"

export const HomePage_HIC: FC = () => {
  const [antdMessageCtx, antdMessageCtxHolder] = message.useMessage()

  const [sites, getSites] = useUsercommSitesBimodal()
  const [syntheticImpacts, getSyntheticImpacts] =
    useUsercommSyntheticImpactWithEmbeddedReferencesBimodal()

  const [deletedSiteAck, deleteSite] = useUsercommDeleteSiteBimodal()

  const [summaryTableType, setSummaryTableType] = useState<ESummaryTableType>(
    ESummaryTableType.Sites,
  )
  const [summaryTableFilterType, setSummaryTableFilterType] = useState<ESummaryTableFilterType>(
    ESummaryTableFilterType.Enterprise,
  )

  const [siteInitFormModalIsOpen, setSiteInitFormModalIsOpen] = useState<boolean>(false)

  const { enterpriseUsers, currentUser } = useSyncronizationContext()

  const { bleIsConnected } = useUsercommContextBLE()
  const [stationConfig, getStationConfig] = useUsercommGetStationConfigBLE()

  const location = useLocation()

  useEffect(() => {
    if (location.hash === "") {
      return
    }
    switch (location.hash) {
      case "#sites":
        setSummaryTableType(ESummaryTableType.Sites)
        break
      case "#impacts":
        setSummaryTableType(ESummaryTableType.Impacts)
        break
    }
  }, [location.hash])

  useEffect(() => {
    getSites()
    setTimeout(() => {
      getSyntheticImpacts()
    }, 1e3)
  }, [])

  useEffect(() => {
    if (bleIsConnected) {
      getStationConfig()
    }
  }, [bleIsConnected])

  useEffect(() => {
    if (syntheticImpacts === null) {
      return
    }
    // console.log("HomePage: impacts", syntheticImpacts)
  }, [syntheticImpacts])

  const onDeleteSiteCB = useCallback((site: Site) => {
    console.log("HomePage: onDeleteSiteSoft", site)
    deleteSite(site.uuid)
  }, [])

  useEffect(() => {
    if (deletedSiteAck === null) {
      return
    }
    antdMessageCtx.info("Site deleted!")
    getSites()
  }, [deletedSiteAck])

  const _siteIsFilteredOutCB = useCallback(
    (site: Site) => {
      if (site.norm_type === NormType.EN_1176) {
        return true
      }
      if (
        summaryTableFilterType === ESummaryTableFilterType.Device &&
        stationConfig !== null &&
        site.device_sn !== stationConfig.readonly_station_device_sn
      ) {
        return true
      }
      if (
        summaryTableFilterType === ESummaryTableFilterType.User &&
        currentUser !== null &&
        pbUUIDToUuid(site.user_uuid) !== currentUser.UUID
      ) {
        return true
      }
      if (
        summaryTableFilterType === ESummaryTableFilterType.Enterprise &&
        enterpriseUsers !== null
      ) {
        for (let user of enterpriseUsers) {
          if (user.UUID === pbUUIDToUuid(site.user_uuid)) {
            return false
          }
        }
        return true
      }
      return false
    },
    [stationConfig, currentUser, enterpriseUsers, summaryTableFilterType],
  )

  const memoFilteredSites = useMemo(() => {
    if (sites === null) {
      return null
    }
    console.debug(`HomePage: memoFilteredSites: `, {
      summaryTableFilterType: summaryTableFilterType,
      stationConfig: stationConfig?.toObject(),
      currentUser: currentUser,
    })
    let t0 = Date.now()
    let filteredSites: Site[] = []
    for (let site of sites) {
      if (_siteIsFilteredOutCB(site)) {
        continue
      }
      filteredSites.push(site)
    }
    let t1 = Date.now()
    console.debug(`HomePage: memoFilteredSites: filtering took ${t1 - t0} ms`)
    return filteredSites
  }, [
    sites,
    stationConfig,
    currentUser,
    enterpriseUsers,
    summaryTableFilterType,
    _siteIsFilteredOutCB,
  ])

  const memoFilteredSyntheticImpacts = useMemo(() => {
    if (syntheticImpacts === null) {
      return null
    }
    let filteredSyntheticImpacts: SyntheticImpactWithEmbeddedReferences[] = []
    for (let impact of syntheticImpacts) {
      if (impact.site === null) {
        // always show unassigned impacts
        filteredSyntheticImpacts.push(impact)
        continue
      }
      if (_siteIsFilteredOutCB(impact.site)) {
        continue
      }
      filteredSyntheticImpacts.push(impact)
    }
    return filteredSyntheticImpacts
  }, [syntheticImpacts, _siteIsFilteredOutCB])

  const memoSummaryTableSites = useMemo(() => {
    return <SitesSummaryTable sites={memoFilteredSites} onDeleteSite={onDeleteSiteCB} />
  }, [memoFilteredSites, onDeleteSiteCB])

  const memoSummaryTableImpacts = useMemo(() => {
    return <ImpactsSummaryTable impacts={memoFilteredSyntheticImpacts} />
  }, [memoFilteredSyntheticImpacts])

  const memoSummaryImpactsMap = useMemo(() => {
    return <SitesClusterMap impacts={memoFilteredSyntheticImpacts} sites={memoFilteredSites} />
  }, [memoFilteredSyntheticImpacts, memoFilteredSites])

  return (
    <FlexCol
      style={{
        width: "100%",
        gap: CENTRAL_CONTAINER_GAP,
      }}
    >
      {antdMessageCtxHolder}
      <Modal
        open={siteInitFormModalIsOpen}
        width={"90vw"}
        title="New mission"
        onCancel={() => {
          setSiteInitFormModalIsOpen(false)
        }}
        onOk={() => {
          setSiteInitFormModalIsOpen(false)
        }}
        footer={null}
        style={{ top: 20 }}
        // centered
      >
        <SiteInitForm sites={sites} onToggle={setSiteInitFormModalIsOpen} />
      </Modal>
      <FlexRow
        style={{
          width: "100%",
          justifyContent: "center",
        }}
      >
        <Radio.Group
          buttonStyle="solid"
          size="large"
          value={summaryTableFilterType}
          onChange={(e) => {
            setSummaryTableFilterType(e.target.value)
          }}
        >
          <Radio.Button value={ESummaryTableFilterType.Device}>
            <Translated keyEn="Device" />
          </Radio.Button>
          <Radio.Button value={ESummaryTableFilterType.User}>
            <Translated keyEn="User" />
          </Radio.Button>
          <Radio.Button value={ESummaryTableFilterType.Enterprise}>
            <Translated keyEn="Enterprise" />
          </Radio.Button>
        </Radio.Group>
      </FlexRow>
      {memoSummaryImpactsMap}
      <Tabs
        activeKey={summaryTableType}
        onChange={(key) => {
          history.pushState(null, "", `#${key}`)
          setSummaryTableType(key as ESummaryTableType)
        }}
        destroyInactiveTabPane
        centered
        items={[
          {
            key: ESummaryTableType.Sites,
            label: <Translated keyEn="Sites" />,
            children: memoSummaryTableSites,
          },
          {
            key: ESummaryTableType.Impacts,
            label: <Translated keyEn="Impacts" />,
            children: memoSummaryTableImpacts,
          },
        ]}
      />
      <DataCloudSyncWidget />
      <FloatButton
        type="primary"
        style={{
          width: 60,
          height: 60,
          right: "5vw",
          bottom: 60,
        }}
        onClick={() => {
          setSiteInitFormModalIsOpen(true)
        }}
        description={
          <Tooltip title={<Translated keyEn="New mission" />}>
            <PlusOutlined style={{ fontSize: "2rem" }} />
          </Tooltip>
        }
      >
        <Translated keyEn="NEW" />
      </FloatButton>
    </FlexCol>
  )
}
